import axios, { type InternalAxiosRequestConfig } from 'axios';
import { store } from '../store/store';
import { logout } from '../store/slices/authSlice';

const AUTH_ERROR_CODES = [
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_NOT_ACTIVE',
  'AUTH_ERROR',
  'MISSING_TOKEN',
  'INVALID_USER',
] as const;

// Flag to prevent multiple simultaneous logout processes
let isLoggingOut = false;

export function isAuthFailure(status?: number, errorCode?: string): boolean {
  // Only treat 401 as an auth failure. 403 is authorization (resource-level)
  // and shouldn't log the user out. Substring matches on the error message
  // ("invalid", "expired", "token") were catching unrelated 400 validation
  // errors and silently logging the user out.
  return (
    status === 401 ||
    (typeof errorCode === 'string' &&
      (AUTH_ERROR_CODES as readonly string[]).includes(errorCode))
  );
}

type AuthErrorLike = {
  response?: {
    status?: number;
    data?: { code?: string };
  };
};

export function handleAuthResponseError(error: AuthErrorLike) {
  const status = error.response?.status;
  const errorCode = error.response?.data?.code;

  if (isAuthFailure(status, errorCode) && !isLoggingOut) {
    isLoggingOut = true;

    console.log('Authentication error detected:', {
      status,
      errorCode
    });

    // Show notification to user
    if (window.location.pathname !== '/login') {
      // Alert removed - authentication errors handled silently
    }

    // Logout user and clear state
    store.dispatch(logout());

    // Reset the flag after a short delay
    setTimeout(() => {
      isLoggingOut = false;
    }, 1000);

    // Only redirect if not already on login page to avoid redirect loops
    if (window.location.pathname !== '/login') {
      // Use replace instead of href to avoid adding to browser history
      window.location.replace('/login');
    }
  }

  return Promise.reject(error);
}

export function attachAuthHeader(config: InternalAxiosRequestConfig) {
  const state = store.getState();
  const token = state.auth.token;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}

// Response interceptor to handle token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => handleAuthResponseError(error)
);

// Request interceptor to ensure token is always included
axios.interceptors.request.use(
  (config) => attachAuthHeader(config),
  (error) => Promise.reject(error)
);

export default axios;
